import apiClient from '../apiClient';

const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_POSTER_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Hero poster/kapak görseli yükle (Supabase Storage)
 * @param {File} file - JPG/PNG/WebP görsel dosyası
 * @returns {Promise<{poster_url: string}>}
 */
export const uploadHeroPosterFile = async (file) => {
  if (!file || !(file instanceof File)) {
    throw new Error('Geçerli bir dosya seçin.');
  }
  if (file.size > MAX_POSTER_SIZE) {
    throw new Error('Kapak görseli 5MB\'dan küçük olmalıdır.');
  }
  const formData = new FormData();
  formData.append('file', file, file.name || 'poster.jpg');
  const response = await apiClient.post('/admin/banners/upload-poster', formData);
  return response.data;
};

/**
 * Hero video dosyası yükle (Supabase Storage)
 * @param {File} file - MP4/WebM video dosyası
 * @returns {Promise<{video_url: string}>}
 */
export const uploadHeroVideoFile = async (file) => {
  if (!file || !(file instanceof File)) {
    throw new Error('Geçerli bir dosya seçin.');
  }
  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error('Dosya boyutu 100MB\'dan küçük olmalıdır.');
  }
  const formData = new FormData();
  formData.append('file', file, file.name || 'video.mp4');
  const response = await apiClient.post('/admin/banners/upload-video', formData);
  return response.data;
};

/**
 * Get hero video config (admin)
 * @returns {Promise<Object|null>}
 */
export const getHeroVideoConfig = async () => {
  try {
    const response = await apiClient.get('/admin/banners', {
      params: { type: 'hero-video', limit: 1 },
    });
    const data = response.data?.data ?? response.data;
    const list = Array.isArray(data) ? data : [];
    const withVideo = list.filter((item) => (item?.video_url || item?.video_url_mp4 || '').trim());
    const candidates = withVideo.length ? withVideo : list;
    const banner = candidates.length
      ? [...candidates].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0) || (b.id ?? 0) - (a.id ?? 0)
        )[0]
      : null;
    return banner;
  } catch (error) {
    console.error('Error fetching hero video config:', error);
    return null;
  }
};

/**
 * Create or update hero video banner
 * @param {Object} data
 * @param {string} data.video_url - MP4/YouTube/Vimeo URL
 * @param {string} [data.poster_url] - Poster/cover image URL
 * @param {string} [data.title]
 * @param {string} [data.subtitle]
 * @param {string} [data.cta_text]
 * @param {string} [data.cta_link]
 * @param {boolean} [data.is_active]
 * @returns {Promise<Object>}
 */
export const saveHeroVideoConfig = async (data) => {
  const videoUrl = (data.video_url || '').trim();
  if (!videoUrl) throw new Error('video_url zorunludur');
  const payload = {
    type: 'hero-video',
    video_url: videoUrl,
    poster_url: data.poster_url || null,
    image_url: data.poster_url || videoUrl || '',
    title: data.title || '',
    subtitle: data.subtitle || '',
    cta: data.cta_text || 'Alışverişe Başla',
    cta_text: data.cta_text || 'Alışverişe Başla',
    link_url: data.cta_link || '/',
    is_active: data.is_active !== false,
    order: 1,
  };
  const response = await apiClient.post('/admin/banners/hero-video/upsert', payload);
  return response.data;
};
